import { ProficiencyLevel } from './employee';

// ============================================================================
// Enums
// ============================================================================

// ..\Mowazaf.Server\Data\PublicProfile\Entities\Enums.cs
/**
 * Profile publication status
 */
export enum ProfileStatus {
    Draft = 'Draft',
    PendingApproval = 'PendingApproval',
    Active = 'Active',
    Disabled = 'Disabled'
}

/**
 * Field governance status for persona field rules
 */
export enum FieldGovernanceStatus {
    Allowed = 'Allowed',
    Forbidden = 'Forbidden',
    Hidden = 'Hidden'
}

/**
 * Approval workflow status
 */
export enum ApprovalStatus {
    Pending = 'Pending',
    Approved = 'Approved',
    Rejected = 'Rejected'
}

/**
 * Profile audit log action types
 */
export enum ProfileAuditAction {
    Created = 'Created',
    Updated = 'Updated',
    Published = 'Published',
    Unpublished = 'Unpublished',
    SlugRegenerated = 'SlugRegenerated',
    ApprovalRequested = 'ApprovalRequested',
    Approved = 'Approved',
    Rejected = 'Rejected',
    Deleted = 'Deleted'
}

// ============================================================================
// View Models
// ============================================================================

// ..\Mowazaf.Server\Data\PublicProfile\ViewModels\PublicProfileView.cs
/**
 * Basic view of a public profile
 */
export interface PublicProfileView {
    id: number;
    userId: string;
    personaId: number;
    personaName: string;
    slug: string;
    isPublic: boolean;
    status: string;
    bio?: string;
    customJobTitle?: string;
    customLocation?: string;
    customCompanyName?: string;
    customEmail?: string;
    customPhone?: string;
    showEmail: boolean;
    showPhone: boolean;
    showSkills: boolean;
    showAchievements: boolean;
    colorScheme?: string;
    language?: string;
    createdOn: Date;
    updatedOn?: Date;
}

// ..\Mowazaf.Server\Data\PublicProfile\ViewModels\PublicProfileDetailView.cs
/**
 * User information summary for the public profile
 */
export interface ProfileUserSummary {
    id: string;
    firstName?: string;
    lastName?: string;
    fullName?: string;
    title?: string;
    imageUrl?: string;
    email?: string;
    phoneNumber?: string;
    linkedInUrl?: string;
    gender?: string;
    departmentId?: number;
    departmentName?: string;
    timeZone?: string;
}

/**
 * Company information summary for the public profile
 */
export interface ProfileCompanySummary {
    id: number;
    name: string;
    imageUrl?: string;
    email?: string;
    websiteLink?: string;
    address?: string;
    phoneNumber?: string;
    primaryColor?: string;
    countryId?: number;
    countryName?: string;
}

/**
 * Skill view for public profile display
 */
export interface ProfileSkillView {
    id: number;
    skillId: number;
    skillName: string;
    skillDescription?: string;
    proficiency: ProficiencyLevel;
    proficiencyDisplay: string;
    endorsementCount: number;
    createdOn: Date;
}

/**
 * Achievement view for public profile display
 */
export interface ProfileAchievementView {
    id: number;
    title: string;
    description?: string;
    date?: string;
    iconName?: string;
    displayOrder: number;
}

/**
 * Social link view for public profile display
 */
export interface ProfileSocialLinkView {
    id: number;
    platform: string;
    url: string;
    iconName?: string;
    displayOrder: number;
}

/**
 * Comprehensive detail view for a public profile including all related data
 */
export interface PublicProfileDetailView {
    // Profile Core
    id: number;
    slug: string;
    isPublic: boolean;
    status: string;
    bio?: string;
    customJobTitle?: string;
    customLocation?: string;
    customCompanyName?: string;
    customEmail?: string;
    customPhone?: string;
    colorScheme?: string;
    language?: string;

    // Visibility Settings
    showEmail: boolean;
    showPhone: boolean;
    showSkills: boolean;
    showAchievements: boolean;

    // Persona Information
    personaId: number;
    personaName: string;
    personaDescription?: string;
    templateId?: string;

    // User Information
    user: ProfileUserSummary;

    // Company Information
    company?: ProfileCompanySummary;

    // Skills (only populated if showSkills is true)
    skills: ProfileSkillView[];

    // Achievements (only populated if showAchievements is true)
    achievements: ProfileAchievementView[];

    // Social Links
    socialLinks: ProfileSocialLinkView[];

    // Company Branding (for public display)
    branding?: CompanyBrandingView;

    // Metadata
    createdOn: Date;
    updatedOn?: Date;
}

// ..\Mowazaf.Server\Data\PublicProfile\ViewModels\ProfilePersonaView.cs
/**
 * View model for profile personas (HR configuration)
 */
export interface ProfilePersonaView {
    id: number;
    companyId: number;
    name: string;
    description?: string;
    isDefault: boolean;
    requiresApproval: boolean;
    allowSlugRegeneration: boolean;
    defaultColorScheme?: string;
    templateId?: string;
    // Branding override
    brandingLogoUrl?: string;
    brandingPrimaryColor?: string;
    brandingSecondaryColor?: string;
    profileCount: number;
    createdOn: Date;
}

// ..\Mowazaf.Server\Data\PublicProfile\ViewModels\ProfileContactView.cs
/**
 * View model for profile contact submissions
 */
export interface ProfileContactView {
    id: number;
    profileId: number;
    fullName: string;
    email: string;
    phone?: string;
    company?: string;
    message?: string;
    submittedAt: Date;
    source?: string;
}

// ============================================================================
// Input Models
// ============================================================================

// ..\Mowazaf.Server\Data\PublicProfile\InputModels\PublicProfileInput.cs
/**
 * Input model for creating or updating a public profile
 */
export interface PublicProfileInput {
    personaId: number;
    bio?: string;
    customJobTitle?: string;
    customLocation?: string;
    customCompanyName?: string;
    customEmail?: string;
    customPhone?: string;
    showEmail: boolean;
    showPhone: boolean;
    showSkills: boolean;
    showAchievements: boolean;
    colorScheme?: string;
    language?: string;
}

// ..\Mowazaf.Server\Data\PublicProfile\InputModels\ProfilePersonaInput.cs
/**
 * Input model for creating or updating a persona
 */
export interface ProfilePersonaInput {
    name: string;
    description?: string;
    isDefault: boolean;
    requiresApproval: boolean;
    allowSlugRegeneration: boolean;
    defaultColorScheme?: string;
    templateId?: string;
    // Branding override
    brandingLogoUrl?: string;
    brandingPrimaryColor?: string;
    brandingSecondaryColor?: string;
}

// ..\Mowazaf.Server\Data\PublicProfile\InputModels\ProfileContactInput.cs
/**
 * Input model for contact form submission from profile visitors
 */
export interface ProfileContactInput {
    profileSlug: string;
    fullName: string;
    email: string;
    phone?: string;
    company?: string;
    message?: string;
    source?: string;
}

// ..\Mowazaf.Server\Data\PublicProfile\InputModels\ProfileSocialLinkInput.cs
/**
 * Input model for creating or updating a social link on a profile
 */
export interface ProfileSocialLinkInput {
    profileId: number;
    platform: string;
    url: string;
    iconName?: string;
    displayOrder: number;
}

// ..\Mowazaf.Server\Data\PublicProfile\InputModels\ProfileAchievementInput.cs
/**
 * Input model for creating or updating an achievement on a profile
 */
export interface ProfileAchievementInput {
    profileId: number;
    title: string;
    description?: string;
    date?: string;
    iconName?: string;
    displayOrder: number;
}

// ============================================================================
// HR Admin Settings - View Models
// ============================================================================

// ..\Mowazaf.Server\Data\PublicProfile\ViewModels\CompanyProfileSettingsView.cs
/**
 * View model for company-wide public profile settings
 */
export interface CompanyProfileSettingsView {
    id: number;
    companyId: number;
    // Global Policy
    publicProfilesEnabled: boolean;
    allowEmployeeToggle: boolean;
    // Branding
    companyLogoUrl?: string;
    primaryColor: string;
    secondaryColor: string;
    disclaimerText?: string;
    footerText?: string;
    showCompanyLogo: boolean;
    showDisclaimer: boolean;
    createdOn: Date;
    updatedOn?: Date;
}

// ..\Mowazaf.Server\Data\PublicProfile\ViewModels\DepartmentProfileSettingsView.cs
/**
 * View model for per-department profile settings
 */
export interface DepartmentProfileSettingsView {
    id: number;
    companyId: number;
    departmentId: number;
    departmentName: string;
    profilesEnabled: boolean;
    requiresApproval: boolean;
    profileCount: number;
}

// ..\Mowazaf.Server\Data\PublicProfile\ViewModels\ProfileFieldGovernanceView.cs
/**
 * View model for field governance rules
 */
export interface ProfileFieldGovernanceView {
    id: number;
    personaId: number;
    fieldKey: string;
    status: FieldGovernanceStatus;
    isMandatory: boolean;
    requiresHRApproval: boolean;
    requiresManagerApproval: boolean;
}

// ..\Mowazaf.Server\Data\PublicProfile\ViewModels\CompanyAnalyticsView.cs
/**
 * View model for company analytics
 */
export interface CompanyAnalyticsView {
    totalProfiles: number;
    activeProfiles: number;
    totalViews: number;
    totalShares: number;
    totalContactSubmissions: number;
    totalQRScans: number;
    dailyTrends: DailyAnalyticsView[];
    topViewedProfiles: TopProfileView[];
}

export interface DailyAnalyticsView {
    date: string;
    views: number;
    shares: number;
    contacts: number;
    qrScans: number;
}

export interface TopProfileView {
    profileId: number;
    employeeName: string;
    profileSlug?: string;
    viewCount: number;
}

// ..\Mowazaf.Server\Data\PublicProfile\ViewModels\ProfileAnalyticsSummaryView.cs
/**
 * View model for individual profile analytics summary
 */
export interface ProfileAnalyticsSummaryView {
    profileId: number;
    employeeName: string;
    profileSlug?: string;
    totalViews: number;
    totalShares: number;
    totalContacts: number;
    totalQRScans: number;
    lastViewDate?: string;
}

// ..\Mowazaf.Server\Data\PublicProfile\ViewModels\ProfileAuditLogView.cs
/**
 * View model for profile audit log entries
 */
export interface ProfileAuditLogView {
    id: number;
    profileId: number;
    employeeName?: string;
    profileSlug?: string;
    action: ProfileAuditAction;
    fieldChanged?: string;
    oldValue?: string;
    newValue?: string;
    actorUserId?: string;
    actorName?: string;
    createdOn: Date;
    ipAddress?: string;
}

// ..\Mowazaf.Server\Data\PublicProfile\ViewModels\ProfileDirectoryItemView.cs
/**
 * View model for profile directory listing
 */
export interface ProfileDirectoryItemView {
    id: number;
    ownerId: string;
    ownerName: string;
    ownerEmail?: string;
    ownerPhotoUrl?: string;
    departmentName?: string;
    jobTitle?: string;
    profileSlug?: string;
    isPublic: boolean;
    status: ProfileStatus;
    personaName?: string;
    totalViews: number;
    totalContacts: number;
    createdOn: Date;
    lastModifiedOn?: Date;
}

// ============================================================================
// HR Admin Settings - Input Models
// ============================================================================

// ..\Mowazaf.Server\Data\PublicProfile\InputModels\CompanyProfileSettingsInput.cs
/**
 * Input model for updating company-wide profile settings
 */
export interface CompanyProfileSettingsInput {
    publicProfilesEnabled: boolean;
    allowEmployeeToggle: boolean;
    companyLogoUrl?: string;
    primaryColor: string;
    secondaryColor: string;
    disclaimerText?: string;
    footerText?: string;
    showCompanyLogo: boolean;
    showDisclaimer: boolean;
}

// ..\Mowazaf.Server\Data\PublicProfile\InputModels\DepartmentProfileSettingsInput.cs
/**
 * Input model for updating department profile settings
 */
export interface DepartmentProfileSettingsInput {
    profilesEnabled: boolean;
    requiresApproval: boolean;
}

// ..\Mowazaf.Server\Data\PublicProfile\InputModels\ProfileFieldGovernanceInput.cs
/**
 * Input model for updating field governance rules
 */
export interface ProfileFieldGovernanceInput {
    fieldKey: string;
    status: FieldGovernanceStatus;
    isMandatory: boolean;
    requiresHRApproval: boolean;
    requiresManagerApproval: boolean;
}

// ..\Mowazaf.Server\Data\PublicProfile\InputModels\BulkProfileStatusInput.cs
/**
 * Input model for bulk profile status updates
 */
export interface BulkProfileStatusInput {
    profileIds: number[];
    action: string;
}

// ============================================================================
// Settings Status View (for employee-facing settings enforcement)
// ============================================================================

/**
 * Company branding settings for display on public profiles
 */
export interface CompanyBrandingView {
    companyLogoUrl?: string;
    primaryColor: string;
    secondaryColor: string;
    disclaimerText?: string;
    footerText?: string;
    showCompanyLogo: boolean;
    showDisclaimer: boolean;
}

/**
 * Profile settings status for an employee.
 * Returns whether profiles are enabled, toggle is allowed, and approval required.
 */
export interface ProfileSettingsStatusView {
    /** Whether profiles are enabled (company AND department level) */
    isProfilesEnabled: boolean;
    /** Whether the employee can toggle their profile visibility */
    allowEmployeeToggle: boolean;
    /** Whether publishing requires approval (persona OR department) */
    requiresApproval: boolean;
    /** Human-readable reason if profiles are disabled */
    disabledReason?: string;
}

// ============================================================================
// Persona Settings (merged persona + company defaults)
// ============================================================================

/**
 * View model for persona-specific settings merged with company defaults.
 * Used by the HR Admin UI to display and edit settings per persona.
 */
export interface PersonaSettingsView {
    personaId: number;
    personaName: string;

    // Branding (merged: persona override ?? company default)
    companyLogoUrl?: string;
    primaryColor: string;
    secondaryColor: string;
    showCompanyLogo: boolean;
    showDisclaimer: boolean;
    disclaimerText?: string;
    footerText?: string;

    // Controls (merged)
    allowEmployeeToggle: boolean;
    requiresApproval: boolean;
    allowSlugRegeneration: boolean;

    // Company-wide settings (always company-level, not persona-specific)
    publicProfilesEnabled: boolean;

    // Metadata to indicate if using defaults
    hasBrandingOverride: boolean;
    hasControlOverride: boolean;
}

/**
 * Input model for saving persona-specific settings.
 * Nullable fields indicate "use company default".
 */
export interface PersonaSettingsInput {
    personaId: number;

    // Branding (null = use company default)
    companyLogoUrl?: string | null;
    primaryColor?: string | null;
    secondaryColor?: string | null;
    showCompanyLogo?: boolean | null;
    showDisclaimer?: boolean | null;
    disclaimerText?: string | null;
    footerText?: string | null;

    // Controls
    allowEmployeeToggle?: boolean | null;
    requiresApproval: boolean;
    allowSlugRegeneration: boolean;
}
